import type { SupportingPagesV2RowResult } from '../services/dataforseo';
import type { DomainTopicUrlRow } from './topic-supporting-pages';
import { normalizeUrlForCompare } from './topic-supporting-pages';

export interface TopicOwnershipDomainBlock {
  domain: string;
  isYourSite: boolean;
  rows: DomainTopicUrlRow[];
}

export interface SupportingPagesUnifiedColumn {
  domain: string;
  isYourSite: boolean;
  heroUrl: string;
  heroRow: DomainTopicUrlRow | null;
  supportingRows: DomainTopicUrlRow[];
  v2Row: SupportingPagesV2RowResult | null;
}

function normalizeDomain(domain: string): string {
  return domain.toLowerCase().trim().replace(/^www\./, '');
}

function rowForUrl(rows: DomainTopicUrlRow[], url: string): DomainTopicUrlRow | null {
  const key = normalizeUrlForCompare(url);
  if (!key) return null;
  return rows.find((r) => normalizeUrlForCompare(r.url) === key) ?? null;
}

/** List rows below the hero — excludes the hero URL when it appears in diagnostics. */
function rowsBelowHero(rows: DomainTopicUrlRow[], heroUrl: string): DomainTopicUrlRow[] {
  const heroKey = normalizeUrlForCompare(heroUrl);
  if (!heroKey) return rows;
  return rows.filter((r) => normalizeUrlForCompare(r.url) !== heroKey);
}

function v2RowForDomain(
  v2Rows: SupportingPagesV2RowResult[] | undefined,
  domain: string,
  extractDomain: (url: string) => string
): SupportingPagesV2RowResult | null {
  const key = normalizeDomain(domain);
  if (!key || !v2Rows?.length) return null;
  return (
    v2Rows.find((row) => normalizeDomain(extractDomain(row.competitorUrl)) === key) ?? null
  );
}

export function buildSupportingPagesUnifiedColumns(input: {
  domains: TopicOwnershipDomainBlock[];
  focalUrl: string;
  v2Rows?: SupportingPagesV2RowResult[];
  extractDomain: (url: string) => string;
  filterRows: (rows: DomainTopicUrlRow[]) => DomainTopicUrlRow[];
}): SupportingPagesUnifiedColumn[] {
  const focal = (input.focalUrl || '').trim();
  const columns: SupportingPagesUnifiedColumn[] = [];

  for (const block of input.domains) {
    const supportingRows = input.filterRows(block.rows);

    if (block.isYourSite) {
      const heroUrl = focal || supportingRows[0]?.url || '';
      columns.push({
        domain: block.domain,
        isYourSite: true,
        heroUrl,
        heroRow: rowForUrl(supportingRows, heroUrl) ?? (heroUrl ? { url: heroUrl, seoIntentScore: 1, topics: [] } : null),
        supportingRows: rowsBelowHero(supportingRows, heroUrl),
        v2Row: null
      });
      continue;
    }

    const v2Row = v2RowForDomain(input.v2Rows, block.domain, input.extractDomain);
    const heroUrl = v2Row?.competitorUrl || supportingRows[0]?.url || '';
    columns.push({
      domain: block.domain,
      isYourSite: false,
      heroUrl,
      heroRow: rowForUrl(supportingRows, heroUrl),
      supportingRows: rowsBelowHero(supportingRows, heroUrl),
      v2Row
    });
  }

  return columns.sort((a, b) => {
    if (a.isYourSite !== b.isYourSite) return Number(b.isYourSite) - Number(a.isYourSite);
    return a.domain.localeCompare(b.domain);
  });
}

export function hasMissingSectionGaps(v2Rows: SupportingPagesV2RowResult[] | undefined | null): boolean {
  return (v2Rows || []).some((row) => (row.missingSectionCount ?? 0) > 0);
}

export function hasListInventoryGaps(v2Rows: SupportingPagesV2RowResult[] | undefined | null): boolean {
  return (v2Rows || []).some(
    (row) =>
      !!row.listInventoryDiff?.detected && (row.listInventoryDiff.competitorOnly?.length ?? 0) > 0
  );
}

/** Competitor columns from v2 eval when v1 topic snapshot is missing or empty. */
export function buildSupportingPagesV2OnlyColumns(input: {
  focalUrl: string;
  yourSiteDomain: string;
  v2Rows: SupportingPagesV2RowResult[];
  extractDomain: (url: string) => string;
}): SupportingPagesUnifiedColumn[] {
  const columns: SupportingPagesUnifiedColumn[] = [];
  const focal = (input.focalUrl || '').trim();
  if (focal && input.yourSiteDomain) {
    columns.push({
      domain: input.yourSiteDomain,
      isYourSite: true,
      heroUrl: focal,
      heroRow: { url: focal, seoIntentScore: 1, topics: [] },
      supportingRows: [],
      v2Row: null
    });
  }
  for (const row of input.v2Rows) {
    const domain = normalizeDomain(input.extractDomain(row.competitorUrl));
    if (!domain) continue;
    columns.push({
      domain,
      isYourSite: false,
      heroUrl: row.competitorUrl,
      heroRow: null,
      supportingRows: [],
      v2Row: row
    });
  }
  return columns.sort((a, b) => {
    if (a.isYourSite !== b.isYourSite) return Number(b.isYourSite) - Number(a.isYourSite);
    return a.domain.localeCompare(b.domain);
  });
}
